import React, { useState, useEffect } from 'react';
import {
  Box,
  Switch,
  TextField,
  Select,
  MenuItem,
  Typography,
  Divider
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { setGlobalError } from '../../../Redux/Reducer/GlobalError';
import { useChatbotSettingsContext } from '../../../utils/ContextProviders/ChatbotSettingsContextProvider';
import { getCookie } from '../../../utils';
import { API_BASE_V1 } from '../utils';

const SectionTitle = ({ children }) => (
  <Typography sx={{ fontWeight: 'bold', fontSize: '16px', mb: 1, mt: 3 }}>
    {children}
  </Typography>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontSize: '14px', mb: 0.5 }}>{children}</Typography>
);

const ChatbotSettingsForm = () => {
  const {
    settingsFormData,
    setSettingsFormData,
    usePresetSettings,
    view,
    presetSettings,
    handlePublishUpdates
  } = useChatbotSettingsContext();
  const dispatch = useDispatch();
  const { communityId } = view;
  const [fetchConfig, setFetchConfig] = useState(1);
  const [communityLeadSources, setCommunityLeadSources] = useState([]);

  useEffect(() => {
    if (communityId) {
      if (usePresetSettings) {
        setSettingsFormData(presetSettings);
      } else setFetchConfig(prev => prev + 1);
    }
  }, [usePresetSettings]);

  useEffect(() => {
    if (!communityId) return;
    const fetchConfigDetails = async () => {
      try {
        const response = await fetch(
          `${API_BASE_V1}/communities/${communityId}/chatbot-config`,
          {
            headers: {
              Authorization: `${getCookie('rb_access_token')}`
            }
          }
        );

        const data = await response.json();

        if (response.ok && data.session_data) {
          const sessionData = data.session_data;

          console.log(sessionData);

          const mobileConfig = {
            default_widget: 'booktour',
            show_icon: true,
            widgets_to_show: sessionData.widgets_to_show ?? [],
            left_position: sessionData.left_position ?? null,
            right_position: sessionData.right_position ?? null,
            bottom_position: sessionData.bottom_position ?? null,
            z_index: sessionData.z_index ?? 99999,
            ...(sessionData.mobile || {})
          };

          const mappedSettings = {
            contactUsEnabled: sessionData.widgets_to_show.includes('textus'),
            pricingEnabled: sessionData.widgets_to_show.includes('pna'),
            scheduleTourEnabled:
              sessionData.widgets_to_show.includes('booktour'),
            primaryColor: sessionData.header_color || '#3d3d3d',
            chatIconAlignment:
              sessionData.left_position !== null ? 'Left' : 'Right',
            leftPadding:
              sessionData.left_position !== null
                ? (sessionData.left_position ?? 0) + 20
                : null,
            bottomPadding: (sessionData.bottom_position ?? 0) + 20,
            rightPadding:
              sessionData.right_position !== null
                ? (sessionData.right_position ?? 0) + 20
                : null,
            greetingMessage:
              sessionData.first_message ||
              'Hi there! How can I help you today?',
            isTookBookingAllowed: sessionData.can_book_tour_flag,
            unitDropdown: sessionData.unit_dropdown_toggle,
            defaultSource: sessionData.default_source,
            widgetButtonsOutlined: sessionData.widget_buttons_outlined,
            zIndex: sessionData.z_index || 99999,
            mobile: {
              defaultWidget: mobileConfig.default_widget ?? 'booktour',
              showIcon: mobileConfig.show_icon ?? true,
              contactUsEnabled: mobileConfig.widgets_to_show.includes('textus'),
              pricingEnabled: mobileConfig.widgets_to_show.includes('pna'),
              scheduleTourEnabled:
                mobileConfig.widgets_to_show.includes('booktour'),
              chatIconAlignment:
                mobileConfig.left_position !== null ? 'Left' : 'Right',
              leftPadding:
                mobileConfig.left_position !== null
                  ? (mobileConfig.left_position ?? 0) + 20
                  : null,
              rightPadding:
                mobileConfig.right_position !== null
                  ? (mobileConfig.right_position ?? 0) + 20
                  : null,
              bottomPadding: (mobileConfig.bottom_position ?? 0) + 20,
              zIndex: mobileConfig.z_index || 99999
            }
          };

          setSettingsFormData(mappedSettings);
        } else if (response.status === 404) {
          // No config yet: create it from the client's preset.
          setSettingsFormData(presetSettings);
          handlePublishUpdates(presetSettings);
        } else {
          setSettingsFormData(presetSettings);
          console.error('Failed to load configuration data.');
        }
      } catch (error) {
        setSettingsFormData(presetSettings);
        console.error('Error fetching config details:', error);
        dispatch(
          setGlobalError('An error occurred while fetching configuration data.')
        );
      }
    };

    fetchConfigDetails();

    const fetchLeadSources = async () => {
      try {
        const response = await fetch(
          `${API_BASE_V1}/lead/sources/${communityId}`,
          {
            headers: {
              Authorization: `${getCookie('rb_access_token')}`
            }
          }
        );
        const data = await response.json();

        if (response.ok) {
          setCommunityLeadSources(data?.data);
        } else {
          console.error('Failed to fetch lead sources.');
          dispatch(setGlobalError('Error fetching lead sources.'));
        }
      } catch (error) {
        console.error('Error fetching lead sources:', error);
        dispatch(
          setGlobalError('An error occurred while fetching lead sources.')
        );
      }
    };

    fetchLeadSources();
  }, [communityId, setSettingsFormData, fetchConfig]);

  const handleChange = (field, value) => {
    if (usePresetSettings) return;
    setSettingsFormData(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const handleChangeMobile = (field, value) => {
    if (usePresetSettings) return;
    setSettingsFormData(prevState => ({
      ...prevState,
      mobile: {
        ...prevState.mobile,
        [field]: value
      }
    }));
  };

  const featureRows = [
    { label: 'Contact (or Text Us)', field: 'contactUsEnabled' },
    { label: 'Pricing & Availability (PNA)', field: 'pricingEnabled' },
    { label: 'Schedule Tour', field: 'scheduleTourEnabled' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* General Configuration */}
      <SectionTitle>General Configuration</SectionTitle>
      <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
        <Box>
          <FieldLabel>Primary Color</FieldLabel>
          <TextField
            type="color"
            value={settingsFormData.primaryColor}
            onChange={e => handleChange('primaryColor', e.target.value)}
            disabled={usePresetSettings}
            size="small"
            sx={{ width: 120 }}
          />
        </Box>
        <Box sx={{ width: 345 }}>
          <FieldLabel>Greeting Message</FieldLabel>
          <TextField
            value={settingsFormData.greetingMessage}
            onChange={e => handleChange('greetingMessage', e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={usePresetSettings}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Switch
          checked={settingsFormData.widgetButtonsOutlined}
          onChange={e =>
            handleChange('widgetButtonsOutlined', e.target.checked)
          }
          sx={{ mr: 1 }}
          disabled={usePresetSettings}
        />
        <Typography>Widget Buttons Outline</Typography>
      </Box>

      <Divider />

      {/* Chatbot Features */}
      <SectionTitle>Chatbot Features</SectionTitle>
      <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
        <Box
          sx={{
            minWidth: 200,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
            &nbsp;
          </Typography>
          {featureRows.map(row => (
            <Typography key={row.field} sx={{ lineHeight: '38px' }}>
              {row.label}
            </Typography>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            alignItems: 'center'
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
            Desktop
          </Typography>
          {featureRows.map(row => (
            <Switch
              key={`desktop-${row.field}`}
              checked={settingsFormData[row.field]}
              onChange={e => handleChange(row.field, e.target.checked)}
              disabled={usePresetSettings}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            alignItems: 'center'
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
            Mobile
          </Typography>
          {featureRows.map(row => (
            <Switch
              key={`mobile-${row.field}`}
              checked={settingsFormData.mobile[row.field]}
              onChange={e => handleChangeMobile(row.field, e.target.checked)}
              disabled={usePresetSettings}
            />
          ))}
        </Box>
      </Box>

      <Divider />

      {/* Mobile Features */}
      <SectionTitle>Mobile Features</SectionTitle>
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ width: 250 }}>
          <FieldLabel>Default Widget</FieldLabel>
          <Select
            value={settingsFormData.mobile?.defaultWidget || ''}
            onChange={e => handleChangeMobile('defaultWidget', e.target.value)}
            fullWidth
            disabled={usePresetSettings}
            size="small"
          >
            <MenuItem value="booktour">Book Tour</MenuItem>
            <MenuItem value="pna">Pricing & Availability</MenuItem>
            <MenuItem value="chat">Chat</MenuItem>
            <MenuItem value="textus">Text Us / Contact Us</MenuItem>
          </Select>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2.5 }}>
          <Switch
            checked={settingsFormData.mobile?.showIcon}
            onChange={e => handleChangeMobile('showIcon', e.target.checked)}
            sx={{ mr: 1 }}
            disabled={usePresetSettings}
          />
          <Typography>Show icon in button</Typography>
        </Box>
      </Box>

      <Divider />

      {/* Layout & Position */}
      <SectionTitle>Layout & Position</SectionTitle>
      <Box sx={{ display: 'flex', gap: 6, mb: 3 }}>
        {/* Desktop column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
            Desktop
          </Typography>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Alignment</FieldLabel>
            <Select
              value={settingsFormData.chatIconAlignment || ''}
              onChange={e => handleChange('chatIconAlignment', e.target.value)}
              fullWidth
              disabled={usePresetSettings}
              size="small"
            >
              <MenuItem value="Left">Left</MenuItem>
              <MenuItem value="Right">Right</MenuItem>
            </Select>
          </Box>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Side Padding (px)</FieldLabel>
            <TextField
              type="number"
              value={
                settingsFormData.chatIconAlignment === 'Left'
                  ? (settingsFormData.leftPadding ?? '')
                  : (settingsFormData.rightPadding ?? '')
              }
              onChange={e =>
                handleChange(
                  settingsFormData.chatIconAlignment === 'Left'
                    ? 'leftPadding'
                    : 'rightPadding',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              fullWidth
              disabled={usePresetSettings}
              size="small"
            />
          </Box>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Bottom Padding (px)</FieldLabel>
            <TextField
              type="number"
              value={settingsFormData.bottomPadding ?? ''}
              onChange={e =>
                handleChange(
                  'bottomPadding',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              fullWidth
              disabled={usePresetSettings}
              size="small"
            />
          </Box>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Z-Index</FieldLabel>
            <TextField
              type="number"
              value={settingsFormData.zIndex ?? ''}
              onChange={e =>
                handleChange(
                  'zIndex',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              fullWidth
              disabled={usePresetSettings}
              size="small"
            />
          </Box>
        </Box>

        {/* Mobile column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
            Mobile
          </Typography>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Alignment</FieldLabel>
            <Select
              value={settingsFormData.mobile?.chatIconAlignment || ''}
              onChange={e =>
                handleChangeMobile('chatIconAlignment', e.target.value)
              }
              fullWidth
              disabled={usePresetSettings}
              size="small"
            >
              <MenuItem value="Left">Left</MenuItem>
              <MenuItem value="Right">Right</MenuItem>
            </Select>
          </Box>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Side Padding (px)</FieldLabel>
            <TextField
              type="number"
              value={
                settingsFormData.mobile?.chatIconAlignment === 'Left'
                  ? (settingsFormData.mobile?.leftPadding ?? '')
                  : (settingsFormData.mobile?.rightPadding ?? '')
              }
              onChange={e =>
                handleChangeMobile(
                  settingsFormData.mobile?.chatIconAlignment === 'Left'
                    ? 'leftPadding'
                    : 'rightPadding',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              fullWidth
              disabled={usePresetSettings}
              size="small"
            />
          </Box>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Bottom Padding (px)</FieldLabel>
            <TextField
              type="number"
              value={settingsFormData.mobile?.bottomPadding ?? ''}
              onChange={e =>
                handleChangeMobile(
                  'bottomPadding',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              fullWidth
              disabled={usePresetSettings}
              size="small"
            />
          </Box>
          <Box sx={{ width: 160 }}>
            <FieldLabel>Z-Index</FieldLabel>
            <TextField
              type="number"
              value={settingsFormData.mobile?.zIndex ?? ''}
              onChange={e =>
                handleChangeMobile(
                  'zIndex',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              fullWidth
              disabled={usePresetSettings}
              size="small"
            />
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Lead Source */}
      <SectionTitle>Lead Source</SectionTitle>
      <Box sx={{ width: 250, mb: 3 }}>
        <FieldLabel>Default Lead Source</FieldLabel>
        <Select
          value={settingsFormData.defaultSource || ''}
          onChange={e => handleChange('defaultSource', e.target.value)}
          fullWidth
          disabled={usePresetSettings}
          size="small"
        >
          {communityLeadSources?.length > 0 &&
            communityLeadSources?.map(source => (
              <MenuItem key={source?.sourceId} value={source?.sourceName}>
                {source?.sourceName}
              </MenuItem>
            ))}
        </Select>
      </Box>

      <Divider />

      {/* Advanced Settings */}
      <SectionTitle>Advanced Settings</SectionTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={settingsFormData.isTookBookingAllowed}
            onChange={e =>
              handleChange('isTookBookingAllowed', e.target.checked)
            }
            sx={{ mr: 1 }}
            disabled={usePresetSettings}
          />
          <Typography>Schedule Tour (via Chatbot)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={settingsFormData.unitDropdown}
            onChange={e => handleChange('unitDropdown', e.target.checked)}
            sx={{ mr: 1 }}
            disabled={usePresetSettings}
          />
          <Typography>Unit Dropdown in Schedule Tour Widget</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatbotSettingsForm;
